import React from 'react';
import { AlertTriangle, Check, Loader2, X } from 'lucide-react';

type Variant = 'danger' | 'default';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  variant?: Variant;
}

export default function ConfirmDialog({
  open,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  loading = false,
  variant = 'default',
}: ConfirmDialogProps) {
  if (!open) return null;

  const confirmStyles =
    variant === 'danger'
      ? 'bg-red-600 hover:bg-red-700 text-white'
      : 'bg-black hover:bg-black/90 text-white';

  const Icon = variant === 'danger' ? AlertTriangle : Check;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="flex items-start gap-3 p-5">
          <div className={`mt-0.5 rounded-full p-2 ${variant === 'danger' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-700'}`}>
            <Icon size={18} />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold">{title}</h3>
            {description}
          </div>
          <button
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
            onClick={onCancel}
            disabled={loading}
            aria-label="Close"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex items-center justify-end gap-2 border-t p-4">
          <button
            className="rounded-xl border px-4 py-2 hover:bg-gray-50"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 ${confirmStyles} disabled:opacity-60`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : null}
            <span>{confirmText}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
